import React from 'react';
import PropTypes from 'prop-types';

import withProfile from 'hocs/withProfile';

import typograf from 'utils/typograf';
import getFileName from 'utils/getFileName';
import Editor from 'editor/export/Editor';

import Loader from 'components/Loader';
import IconClose from 'icons/Close';

import PublicationLock from 'admin-editor/containers/PublicationLock';

import TextBlock from 'admin-editor/components/TextBlock';
import TrafficLabel from 'admin-editor/components/TrafficLabel';
import PublicationCoverBlock from 'admin-editor/components/PublicationCoverBlock';
import PublicationShareBlock from 'admin-editor/components/PublicationShareBlock';
import PublicationParams from 'admin-editor/components/PublicationParams';
import ConfirmPinModal from 'admin-editor/components/ConfirmPinModal';
import CopyToClipboard from 'admin-editor/components/CopyToClipboard';

import CenteredContainer from 'styled/Page/CenteredContainer';
import FilterWrapper from 'styled/Page/FilterWrapper';
import RightColumn from 'styled/Page/RightColumn';
import PrimaryButton from 'styled/Buttons/PrimaryButton';
import SecondaryButton from 'styled/Buttons/SecondaryButton';
import StatusText from 'styled/Page/StatusText';
import SimpleButton from 'styled/Buttons/SimpleButton';
import ErrorText from 'styled/Page/ErrorText';
import Container from 'styled/Editor/Container';

import States from './States';

const STATES = {
  draft: {
    name: 'Черновик',
  },
  published: {
    name: 'Опубликована',
    message: 'Публикация опубликована',
  },
  processing: {
    name: 'В обработке',
    message: 'Публикация успешно сохранена и отправлена на обработку',
  },
  hidden: {
    name: 'Скрыта',
    message: 'Публикация успешно скрыта',
  },
  is_editor_approved: {
    message: 'Публикация успешно одобрена редактором',
  },
  is_corrector_approved: {
    message: 'Публикация успешно одобрена корректором',
  },
};

const EMOTIONS = ['red', 'orange', 'yellow', 'lime', 'green'];

const TIME_OFFSET = 3 * 3600 * 1000;

export class PublicationForm extends React.PureComponent {
  editor = React.createRef();

  pinPayload = undefined;

  constructor(props) {
    super(props);

    this.handleTitleChange = this.handleInputChange.bind(this, 'title');
    this.handleShareTitleChange = this.handleInputChange.bind(
      this,
      'shareTitle'
    );
    this.handleShareDescriptionChange = this.handleInputChange.bind(
      this,
      'shareDescription'
    );
    this.handleChatTitleChange = this.handleInputChange.bind(this, 'chatTitle');
    this.handleSubtitleChange = this.handleInputChange.bind(this, 'subtitle');
    this.handleEditorApproveStateChange = this.handleApproveStateChange.bind(
      this,
      'is_editor_approved'
    );
    this.handleCorrectorApproveStateChange = this.handleApproveStateChange.bind(
      this,
      'is_corrector_approved'
    );
  }

  state = {
    loading: false,
    errors: [],

    title: this.props.title || '',
    subtitle: this.props.subtitle || '',
    shareTitle: this.props.shareTitle || '',
    shareDescription: this.props.shareDescription || '',
    chatTitle: this.props.chatTitle || '',
    postType: this.props.postType || 'short',
    publishedAt: this.publishedAt,
    positiveReaction: this.props.positiveReaction || '',
    negativeReaction: this.props.negativeReaction || '',
    destination: this.props.destination || 'everywhere',
    isPinned: this.props.isPinned || false,
    isPush: this.props.isPush || false,
    isAdult: this.props.isAdult || false,
    isAuthorizedOnly: this.props.isAuthorizedOnly || false,
    isNoise: this.props.isNoise || false,
    isPartner: this.props.isPartner || false,
    defaultContent: this.props.content || '[{"type":"TEXT","value":""}]',
    image: this.props.image,
    mascotType: this.props.mascotType,
    mascotPhrase: this.props.mascotPhrase,
    isMascotActive: this.props.isMascotActive,
    emotion: EMOTIONS.indexOf(this.props.emotion),
    snoozes: this.props.snoozes,
    author: this.props.author || '',
    sourceLink: this.props.sourceLink || '',
  };

  componentDidUpdate(prevProps) {
    if (prevProps.publishedAt !== this.props.publishedAt) {
      this.setState({ publishedAt: this.publishedAt }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.image !== this.props.image) {
      this.setState({ image: this.props.image }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.title !== this.props.title) {
      this.setState({ title: this.props.title }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.subtitle !== this.props.subtitle) {
      this.setState({ subtitle: this.props.subtitle }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.shareTitle !== this.props.shareTitle) {
      this.setState({ shareTitle: this.props.shareTitle }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.shareDescription !== this.props.shareDescription) {
      this.setState({ shareDescription: this.props.shareDescription }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.chatTitle !== this.props.chatTitle) {
      this.setState({ chatTitle: this.props.chatTitle }); // eslint-disable-line react/no-did-update-set-state
    }

    if (prevProps.mascotPhrase !== this.props.mascotPhrase) {
      this.setState({ mascotPhrase: this.props.mascotPhrase }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  get isContentValid() {
    if (!this.editor.current) return true;
    return this.editor.current.isValid();
  }

  get data() {
    return {
      type: 'post',
      attributes: {
        title: typograf.execute(this.state.title),
        content:
          this.state.postType === 'iStory'
            ? ''
            : this.editor.current && this.editor.current.getContent(),
        subtitle: typograf.execute(this.state.subtitle),
        share_title: typograf.execute(this.state.shareTitle),
        share_description: typograf.execute(this.state.shareDescription),
        chat_title: typograf.execute(this.state.chatTitle),
        post_type: this.state.postType,
        published_at: this.state.publishedAt,
        is_push: this.state.postType === 'iStory' ? false : this.state.isPush,
        is_adult: this.state.isAdult,
        is_noise: this.state.isNoise,
        is_partner: this.state.isPartner,
        is_authorized_only:
          this.state.postType !== 'longread'
            ? false
            : this.state.isAuthorizedOnly,
        is_pinned: this.state.isPinned,
        positive_reaction: this.state.positiveReaction,
        negative_reaction: this.state.negativeReaction,
        destination: this.state.destination,
        mascot_type: this.state.mascotType,
        mascot_phrase: this.state.isMascotActive
          ? typograf.execute(this.state.mascotPhrase)
          : '',
        is_mascot_active: this.state.isMascotActive,
        emotion: EMOTIONS[this.state.emotion],
        author: this.state.postType === 'longread' ? this.state.author : '',
        source_link: this.state.sourceLink,
        is_editor_approved: this.props.isEditorApproved,
        is_corrector_approved: this.props.isCorrectorApproved,
      },
      relationships: {
        image: {
          data: this.state.image
            ? {
                type: 'image',
                attributes: {
                  filename: this.state.image.filename,
                  file_type: this.state.image.fileType,
                  file_size: this.state.image.fileSize,
                  width: this.state.image.width,
                  height: this.state.image.height,
                  medialib_item_id: this.state.image.medialibItemId,
                  title: typograf.execute(this.state.image.title),
                },
              }
            : null,
        },
        snoozes: {
          data: this.state.snoozes.map((snooze) => ({
            type: 'snooze',
            id: snooze.id,
          })),
        },
      },
    };
  }

  get publishedAt() {
    return this.props.publishedAt
      ? new Date(this.props.publishedAt + TIME_OFFSET)
          .toISOString()
          .substr(0, 16)
      : undefined;
  }

  get newPublishedAt() {
    const NOW = new Date(Number(new Date()) + TIME_OFFSET)
      .toISOString()
      .substr(0, 16);

    if (!this.data.attributes.published_at) return NOW;

    return this.data.attributes.published_at;
  }

  get previewLink() {
    let host;

    switch (window.location.hostname) {
      case 'localhost':
        host = 'http://localhost:5050';
        break;
      case 'staging-admin.baza.io':
        host = 'https://staging-web.baza.io';
        break;
      case 'admin.baza.io':
        host = 'https://baza.io';
        break;
      default:
        return '';
    }

    if (!host) return '';
    return `${host}/posts/${this.props.id}?preview=true`;
  }

  handleInputChange = (field, e) => {
    this.setState({ [field]: e.target.value });
  };

  handleFieldChange = (field, value) => {
    this.setState({ [field]: value });
  };

  handleTabClick = (e) => {
    const destination = e.target.id;

    this.setState((prevState) => ({
      destination,
      isAdult: destination === 'application' ? false : prevState.isAdult,
    }));
  };

  handleCheckboxChange = (e) => {
    this.setState({ [e.target.id]: e.target.checked });
  };

  handleCoverLoad = (file) => {
    this.setState({
      image: {
        fileType: file.attributes.content_type,
        filename: getFileName({ url: file.attributes.path, type: '/posts' }),
        fileSize: file.attributes.size,
        width: file.attributes.width,
        height: file.attributes.height,
        medialibItemId: file.id,
        url: file.attributes.path,
        title: '',
      },
    });
  };

  handleCoverTitleChange = (e) => {
    const title = e.target.value;
    this.setState((prevState) => ({ image: { ...prevState.image, title } }));
  };

  handleCoverDelete = () => {
    this.setState({ image: undefined });
  };

  handleSaveClick = () => {
    this.pinPayload = undefined;
    console.log(`data at save ${JSON.stringify(this.data)}`);
    this.props.onSave({
      data: this.data,
      isContentValid: this.isContentValid,
    });
  };

  handleStateChange = (state) => {
    this.pinPayload = { state };

    this.props.onSave({
      data: {
        ...this.data,
        attributes: {
          ...this.data.attributes,
          state,
          published_at:
            state === 'published'
              ? this.newPublishedAt
              : this.data.attributes.published_at,
        },
      },
      message: STATES[state].message,
      isContentValid: this.isContentValid,
    });
  };

  handleApproveStateChange = (state) => {
    this.pinPayload = { [state]: true };
    this.props.onSave({
      data: {
        ...this.data,
        attributes: { ...this.data.attributes, [state]: true },
      },
      message: STATES[state].message,
      isContentValid: this.isContentValid,
    });
  };

  handleSaveAndExitClick = () => {
    this.pinPayload = { exit: true };
    this.props.onSave({
      data: this.data,
      exit: true,
      isContentValid: this.isContentValid,
    });
  };

  handlePinModalClose = () => {
    this.pinPayload = undefined;
    this.setState({ isPinned: false }, this.props.onPinModalClose);
  };

  handlePinSubmit = (e) => {
    e.preventDefault();

    this.props.onSave({
      data: {
        ...this.data,
        attributes: {
          ...this.data.attributes,
          state: this.pinPayload ? this.pinPayload.state : this.props.state,
          is_corrector_approved: this.pinPayload
            ? this.pinPayload.is_corrector_approved
            : this.props.isCorrectorApproved,
          is_editor_approved: this.pinPayload
            ? this.pinPayload.is_editor_approved
            : this.props.isEditorApproved,
          published_at:
            (this.pinPayload && this.pinPayload.state === 'published') ||
            this.props.state === 'published'
              ? this.newPublishedAt
              : this.data.attributes.published_at,
        },
      },
      exit: this.pinPayload ? this.pinPayload.exit : false,
      message: 'Публикация успешно сохранена и закреплена',
      pinConfirmed: true,
    });
  };

  handleEmotionChange = (emotion) => {
    if (
      this.props.can({
        resource: 'publication',
        permission: 'changeEmotion',
        state: this.props.state,
      })
    ) {
      this.setState({ emotion });
    }
  };

  getData = () => this.data;

  render() {
    return (
      <React.Fragment>
        {this.props.id &&
          this.props.can({
            resource: 'publication',
            permission: 'lock',
            state: this.props.state,
          }) && (
            <PublicationLock
              id={this.props.id}
              data={this.getData}
              shouldUnlockOnUnmount={this.props.shouldUnlockOnUnmount}
            />
          )}

        <div style={{ width: '100%' }}>
          <FilterWrapper style={{ justifyContent: 'flex-end' }}>
            {this.props.id && (
              <SecondaryButton
                as="a"
                style={{ textDecoration: 'none' }}
                href={this.previewLink}
                target="_blank"
                rel="nofollow noopener"
              >
                предпросмотр
              </SecondaryButton>
            )}

            {this.props.id && this.props.state === 'published' && (
              <CopyToClipboard
                id={this.props.id}
                isAdult={this.props.isAdult}
              />
            )}

            <PrimaryButton
              type="button"
              style={{ marginLeft: 'auto' }}
              onClick={this.handleSaveClick}
              disabled={
                this.props.loading ||
                !this.props.can({
                  resource: 'publication',
                  permission: 'save',
                  state: this.props.state,
                })
              }
            >
              сохранить
            </PrimaryButton>

            <SecondaryButton
              type="button"
              style={{ marginLeft: '20px' }}
              onClick={this.handleSaveAndExitClick}
              disabled={
                this.props.loading ||
                !this.props.can({
                  resource: 'publication',
                  permission: 'save',
                  state: this.props.state,
                })
              }
            >
              сохранить и выйти
            </SecondaryButton>

            <SimpleButton
              type="button"
              onClick={this.props.onClose}
              style={{ marginLeft: '45px' }}
            >
              <IconClose />
            </SimpleButton>
          </FilterWrapper>

          <div style={{ padding: '30px 32px 0 24px' }}>
            <States>
              <StatusText style={{ marginRight: 'auto' }}>
                Текущее состояние: {STATES[this.props.state].name}
              </StatusText>

              <TrafficLabel
                isEditorApproved={this.props.isEditorApproved}
                isCorrectorApproved={this.props.isCorrectorApproved}
              />
            </States>

            <TextBlock
              value={this.state.title}
              onChange={this.handleTitleChange}
              label="Заголовок"
              maxLength={120}
            />

            <TextBlock
              value={this.state.chatTitle}
              onChange={this.handleChatTitleChange}
              label="Заголовок чата"
              maxLength={250}
            />

            <TextBlock
              value={this.state.subtitle}
              onChange={this.handleSubtitleChange}
              label="Подзаголовок"
            />

            {this.state.postType !== 'iStory' && (
              <Container style={{ minHeight: 'auto' }}>
                <Editor
                  ref={this.editor}
                  defaultContent={this.state.defaultContent}
                  postType={this.state.postType}
                />
              </Container>
            )}

            <PublicationShareBlock
              shareTitle={this.state.shareTitle}
              shareDescription={this.state.shareDescription}
              onShareTitleChange={this.handleShareTitleChange}
              onShareDescriptionChange={this.handleShareDescriptionChange}
            />

            <PublicationCoverBlock
              url={this.state.image ? this.state.image.url : ''}
              fileType={this.state.image && this.state.image.fileType}
              title={this.state.image && this.state.image.title}
              disabled={
                !this.props.can({
                  resource: 'publication',
                  permission: 'changeCover',
                })
              }
              onLoad={this.handleCoverLoad}
              onTitleChange={this.handleCoverTitleChange}
              onDelete={this.handleCoverDelete}
            />
          </div>

          {this.state.errors.length > 0 && (
            <CenteredContainer>
              {this.state.errors.map((error) => (
                <ErrorText role="alert" key={error}>
                  {error}
                </ErrorText>
              ))}
            </CenteredContainer>
          )}

          {this.state.loading && (
            <CenteredContainer>
              <Loader />
            </CenteredContainer>
          )}
        </div>

        <RightColumn>
          <PublicationParams
            type={this.state.postType}
            author={this.state.author}
            sourceLink={this.state.sourceLink}
            publishedAt={this.state.publishedAt}
            positiveReaction={this.state.positiveReaction}
            negativeReaction={this.state.negativeReaction}
            onInputChange={this.handleInputChange}
            onFieldChange={this.handleFieldChange}
            destination={this.state.destination}
            onTabClick={this.handleTabClick}
            onCheckboxChange={this.handleCheckboxChange}
            isPinned={this.state.isPinned}
            isPush={this.state.isPush}
            isAdult={this.state.isAdult}
            isAuthorizedOnly={this.state.isAuthorizedOnly}
            isNoise={this.state.isNoise}
            isPartner={this.state.isPartner}
            mascotType={this.state.mascotType}
            mascotPhrase={this.state.mascotPhrase}
            isMascotActive={this.state.isMascotActive}
            emotion={this.state.emotion}
            onEmotionChange={this.handleEmotionChange}
            snoozes={this.state.snoozes}
            state={this.props.state}
            onStateChange={this.handleStateChange}
            onEditorStateChange={this.handleEditorApproveStateChange}
            onCorrectorStateChange={this.handleCorrectorApproveStateChange}
            isEditorApproved={this.props.isEditorApproved}
            isCorrectorApproved={this.props.isCorrectorApproved}
            onDelete={this.props.onDelete}
          />
        </RightColumn>

        <ConfirmPinModal
          isOpen={this.props.isPinModalOpen}
          onClose={this.handlePinModalClose}
          onSubmit={this.handlePinSubmit}
          loading={this.props.loading}
        />
      </React.Fragment>
    );
  }
}

PublicationForm.propTypes = {
  id: PropTypes.string,
  onSave: PropTypes.func,
  loading: PropTypes.bool,

  title: PropTypes.string,
  subtitle: PropTypes.string,
  shareTitle: PropTypes.string,
  shareDescription: PropTypes.string,
  chatTitle: PropTypes.string,
  content: PropTypes.string,
  isPinned: PropTypes.bool,
  isPush: PropTypes.bool,
  isAdult: PropTypes.bool,
  isAuthorizedOnly: PropTypes.bool,
  isNoise: PropTypes.bool,
  isPartner: PropTypes.bool,
  positiveReaction: PropTypes.string,
  negativeReaction: PropTypes.string,
  destination: PropTypes.oneOf(['everywhere', 'web', 'application']),
  postType: PropTypes.oneOf(['short', 'iStory', 'longread']),
  publishedAt: PropTypes.number,
  isEditorApproved: PropTypes.bool,
  isCorrectorApproved: PropTypes.bool,
  state: PropTypes.oneOf(['draft', 'processing', 'published', 'hidden']),
  image: PropTypes.shape({
    fileType: PropTypes.string,
    filename: PropTypes.string,
    fileSize: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    medialibItemId: PropTypes.string,
    title: PropTypes.string,
  }),
  mascotType: PropTypes.oneOf(['positive', 'negative']),
  mascotPhrase: PropTypes.string,
  isMascotActive: PropTypes.bool,
  emotion: PropTypes.oneOf(EMOTIONS),
  author: PropTypes.string,
  sourceLink: PropTypes.string,
  snoozes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  onClose: PropTypes.func,
  can: PropTypes.func,
  isPinModalOpen: PropTypes.bool,
  onPinModalClose: PropTypes.func,
  onDelete: PropTypes.func,
  shouldUnlockOnUnmount: PropTypes.bool,
};

PublicationForm.defaultProps = {
  id: undefined,
  onSave: () => {},
  loading: false,

  title: '',
  subtitle: '',
  shareTitle: '',
  shareDescription: '',
  chatTitle: '',
  content: '[{"type":"TEXT","value":""}]',
  isPinned: false,
  isPush: false,
  isAdult: false,
  isAuthorizedOnly: false,
  isNoise: false,
  isPartner: false,
  positiveReaction: '',
  negativeReaction: '',
  destination: 'everywhere',
  postType: 'short',
  publishedAt: undefined,
  isEditorApproved: false,
  isCorrectorApproved: false,
  state: 'draft',
  author: '',
  sourceLink: '',
  image: undefined,
  mascotType: 'positive',
  mascotPhrase: '',
  isMascotActive: false,
  emotion: 'yellow',
  snoozes: [],
  onClose: () => {},
  can: () => {},
  isPinModalOpen: false,
  onPinModalClose: () => {},
  onDelete: () => {},
  shouldUnlockOnUnmount: false,
};

export default withProfile(PublicationForm);
